import _ from "lodash";
import { useCallback, useReducer } from "react";
import { ViewOptionStateEnum } from "../../contextual-app-bar/ViewOptionStateEnum";
import { IEditTagSearchResultDeviceDTO } from "../../../services/setpoint-administration-service/Models";

export enum AlarmSetpointUsageTabEnum {
    None = -1,
    Devices = 0,
    Wells = 1,
    TankBatteries = 2,
    Tanks = 3
}

export type AlarmSetpointUsageStateType = {
    activeTab: AlarmSetpointUsageTabEnum;
    objectNameFilter: string;
    tagNameFilter: string;
    viewSelected: boolean;
    viewOptionState: ViewOptionStateEnum;
    expandedObjects: IEditTagSearchResultDeviceDTO[];
};

enum AlarmSetpointUsageActionType {
    ActiveTabChanged = "ACTIVE_TAB_CHANGED",
    ObjectFilterChanged = "OBJECT_FILTER_CHANGED",
    TagFilterChanged = "TAG_FILTER_CHANGED",
    ViewSelectedChanged = "VIEW_SELECTED_CHANGED",
    ResetChanged = "RESET_CHANGED",
    ExpandedObjectsChanged = "EXPANDED_OBJECTS_CHANGED"
}

type AlarmSetpointUsageAction = {
    type: AlarmSetpointUsageActionType.ActiveTabChanged;
    activeTab: AlarmSetpointUsageTabEnum;
} | {
     type: AlarmSetpointUsageActionType.ObjectFilterChanged;
     payload: string;
} | {
    type: AlarmSetpointUsageActionType.TagFilterChanged;
    payload: string;
} | {
    type: AlarmSetpointUsageActionType.ViewSelectedChanged;
    payload: boolean;
} | {
    type: AlarmSetpointUsageActionType.ResetChanged;
    payload: void;
} | {
    type: AlarmSetpointUsageActionType.ExpandedObjectsChanged;
    payload: IEditTagSearchResultDeviceDTO[];
};

const initialState: AlarmSetpointUsageStateType = {
    activeTab: AlarmSetpointUsageTabEnum.None,
    objectNameFilter: '',
    tagNameFilter: '',
    viewSelected: false,
    viewOptionState: ViewOptionStateEnum.Default,
    expandedObjects: []
};

const optionsChanged = (newState: AlarmSetpointUsageStateType, 
    defaultState: AlarmSetpointUsageStateType) => 
    newState.objectNameFilter !== defaultState.objectNameFilter ||
    newState.tagNameFilter !== defaultState.tagNameFilter ||
    newState.viewSelected !== defaultState.viewSelected;

function setpointUsageReducer(state: AlarmSetpointUsageStateType, action: AlarmSetpointUsageAction): AlarmSetpointUsageStateType {
    let copy = _.cloneDeep(state);    

    switch (action.type) {

        case AlarmSetpointUsageActionType.ActiveTabChanged:
            copy.activeTab = action.activeTab;
            break;

        case AlarmSetpointUsageActionType.ObjectFilterChanged:
            copy.objectNameFilter = action.payload;
            break;

        case AlarmSetpointUsageActionType.TagFilterChanged:
            copy.tagNameFilter = action.payload;
            break;

        case AlarmSetpointUsageActionType.ViewSelectedChanged:
            copy.viewSelected = action.payload;
            break;

        case AlarmSetpointUsageActionType.ResetChanged:
            copy = _.cloneDeep(initialState);
            break;

        case AlarmSetpointUsageActionType.ExpandedObjectsChanged:
            copy.expandedObjects = action.payload;
            break;

        default:
            return state;
    }

    copy.viewOptionState = optionsChanged(copy, initialState) ? 
                            ViewOptionStateEnum.Changed : 
                            ViewOptionStateEnum.Default;

    return copy;
}

export type UseAlarmSetpointUsageStateType = {
    state: AlarmSetpointUsageStateType;
    setActiveTab: (activeTab: AlarmSetpointUsageTabEnum) => void;
    setObjectFilter: (value: string) => void;
    setTagFilter: (value: string) => void;
    setViewSelected: (value: boolean) => void;
    resetState: () => void;
    setExpandedObjects: (value: IEditTagSearchResultDeviceDTO[]) => void;
};

export function useAlarmSetpointUsageState(): UseAlarmSetpointUsageStateType {

    const [state, dispatch] = useReducer(setpointUsageReducer, initialState);

    const setActiveTab = useCallback((activeTab: AlarmSetpointUsageTabEnum) => dispatch({
        type: AlarmSetpointUsageActionType.ActiveTabChanged,
        activeTab: activeTab
    }), []);

    const setObjectFilter = useCallback((value: string) => dispatch({
        type: AlarmSetpointUsageActionType.ObjectFilterChanged,
        payload: value
    }), []);

    const setTagFilter = useCallback((value: string) => dispatch({
        type: AlarmSetpointUsageActionType.TagFilterChanged,
        payload: value
    }), []);


    const setViewSelected = useCallback((value: boolean) => dispatch({
        type: AlarmSetpointUsageActionType.ViewSelectedChanged,
        payload: value
    }), []);

    const setExpandedObjects = useCallback((value: IEditTagSearchResultDeviceDTO[]) => dispatch({
        type: AlarmSetpointUsageActionType.ExpandedObjectsChanged,
        payload: value
    }), []);

    const resetState = useCallback(() => dispatch({
        type: AlarmSetpointUsageActionType.ResetChanged,
        payload: undefined
    }), [])

    return {
        state,
        setActiveTab,
        setObjectFilter,
        setTagFilter,
        setViewSelected,
        setExpandedObjects,
        resetState
    };
}
